import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { setOpenSearch } from "../../../../windowManager/windowManagerSlice";
import {
	moveSheetTo,
	selectMemories,
	selectSheets,
	selectTab,
	updateSheetScale,
} from "../../../editorSlice";
import MemoryRef from "../../../interfaces/components/MemoryRef";
import css from "./Search.module.css";

interface Search {
	fixedZoom: () => number;
}

export const Search = ({ fixedZoom }: Search) => {
	const dispatch = useAppDispatch();
	const sheets = useAppSelector(selectSheets);
	const memories = useAppSelector(selectMemories);
	const [searchText, setSearchText] = useState<string>("");
	const [debounceText, setDebounceText] = useState<string>("");
	const [memoryRefs, setMemoryRefs] = useState<
		Array<{ sheet: number; block: MemoryRef }>
	>([]);
	const [current, setCurrent] = useState<number | null>(null);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebounceText(searchText);
		}, 1000);

		return () => {
			clearTimeout(handler);
		};
	}, [searchText]);

	useEffect(() => {
		if (debounceText) {
			const memos: Set<string> = new Set();
			const refs: Array<{ sheet: number; block: MemoryRef }> = [];

			Object.values(memories).forEach((blocks) => {
				blocks
					.filter((block) =>
						block.label.toUpperCase().includes(searchText.toUpperCase()),
					)
					.forEach((block) => {
						memos.add(block.id);
					});
			});

			sheets.forEach((sheet, idx) => {
				const blocks = sheet.memoryRefs
					.filter(
						(memo) => memo.sourceMemoryId && memos.has(memo.sourceMemoryId),
					)
					.map((memo) => ({
						sheet: idx,
						block: memo,
					}));

				refs.push(...blocks);
			});

			setMemoryRefs(refs);
			setCurrent(0);
			if (refs.length > 0) handleMove();
		}
	}, [debounceText]);

	const handleMove = () => {
		if (current == null || !memoryRefs.length) return;

		const block: MemoryRef = memoryRefs[current].block;
		const sheetIndex: number = memoryRefs[current].sheet;

		dispatch(selectTab(sheetIndex));

		const position = {
			x: block.boundingBox.left + block.boundingBox.width,
			y: block.boundingBox.height + block.boundingBox.top,
		};

		dispatch(
			moveSheetTo({
				index: sheetIndex,
				position,
			}),
		);

		const newScale = fixedZoom();

		dispatch(
			updateSheetScale({
				index: sheetIndex,
				scale: newScale,
			}),
		);
	};

	return (
		<div className={css.searchDiv}>
			<input
				id="search-input"
				autoFocus
				className={css.searchInput}
				onChange={(e) => setSearchText(e.target.value)}
				value={searchText}
			/>
			{current != null && (
				<span
					id="span-search-count"
					style={{ color: "white", whiteSpace: "nowrap", marginRight: 4 }}
				>
					{memoryRefs.length
						? `${Number(current) + 1}/ ${memoryRefs.length}`
						: "0/0"}
				</span>
			)}

			<hr className={css.searchHr} />
			<div style={{ display: "flex" }}>
				<button
					className={css.searchButton}
					disabled={memoryRefs.length == 0}
					onClick={() => {
						setCurrent((old) => {
							if (old == 0) return memoryRefs.length - 1;

							return Number(old) - 1;
						});
						handleMove();
					}}
				>
					<svg height={12} width={12}>
						<line
							className={css.searchButtonLine}
							x1="10%"
							x2="50%"
							y1="70%"
							y2="30%"
						/>
						<line
							className={css.searchButtonLine}
							x1="90%"
							x2="50%"
							y1="70%"
							y2="30%"
						/>
					</svg>
				</button>
				<button
					className={css.searchButton}
					disabled={memoryRefs.length == 0}
					onClick={() => {
						setCurrent((old) => {
							if (old == memoryRefs.length - 1) return 0;

							return Number(old) + 1;
						});
						handleMove();
					}}
				>
					<svg height={12} width={12}>
						<line
							className={css.searchButtonLine}
							x1="10%"
							x2="50%"
							y1="30%"
							y2="70%"
						/>
						<line
							className={css.searchButtonLine}
							x1="90%"
							x2="50%"
							y1="30%"
							y2="70%"
						/>
					</svg>
				</button>
			</div>
			<button
				className={css.searchButton}
				onClick={() => {
					dispatch(setOpenSearch(false));
				}}
			>
				<svg height={12} width={12}>
					<line
						className={css.searchButtonLine}
						x1="10%"
						x2="90%"
						y1="20%"
						y2="80%"
					/>
					<line
						className={css.searchButtonLine}
						x1="90%"
						x2="10%"
						y1="20%"
						y2="80%"
					/>
				</svg>
			</button>
		</div>
	);
};
